import { type ReactElement } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useSeo } from './hooks/useSeo';
import { SEO } from './lib/seoTexte';

// Auth & App (eingeloggter Bereich)
import { AuthProvider } from './app/auth/AuthProvider';
import { CompanyProvider } from './app/CompanyProvider';
import { ConfirmProvider } from './app/ConfirmProvider';
import { ToastProvider } from './app/ToastProvider';
import { ThemeProvider } from './app/ThemeProvider';
import RequireAuth from './app/RequireAuth';
import RequireCompany from './app/RequireCompany';
import Login from './app/auth/Login';
import Register from './app/auth/Register';
import ForgotPassword from './app/auth/ForgotPassword';
import SetPassword from './app/auth/SetPassword';
import Onboarding from './app/Onboarding';
import AppLayout from './app/AppLayout';
import Dashboard from './app/Dashboard';
import Customers from './app/customers/Customers';
import CustomerDetail from './app/customers/CustomerDetail';
import Products from './app/products/Products';
import Nachweise from './app/proofs/Nachweise';
import Invoices from './app/invoices/Invoices';
import NewInvoice from './app/invoices/NewInvoice';
import InvoiceDetail from './app/invoices/InvoiceDetail';
import Receipts from './app/receipts/Receipts';
import Cashbook from './app/cashbook/Cashbook';
import KPIs from './app/kpis/KPIs';
import Billing from './app/billing/Billing';
import PartnerProgramm from './app/referrals/Partner';
import Einladung from './pages/Einladung';
import Accounting from './app/accounting/Accounting';
import Taxes from './app/taxes/Taxes';
import BankingApp from './app/banking/Banking';
import Forms from './app/forms/Forms';
import Settings from './app/settings/Settings';
import More from './app/More';

// Layout components
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import CookieConsent from './components/CookieConsent';

// Landing Page sections
import Hero from './components/sections/Hero';
import Features from './components/sections/Features';
import HowItWorks from './components/sections/HowItWorks';
import Pricing from './components/sections/Pricing';
// import Testimonials from './components/sections/Testimonials'; // siehe Hinweis unten
import CallToAction from './components/sections/CallToAction';

// Feature Pages
import FunktionenUebersicht from './pages/funktionen/index';
import Rechnungen from './pages/funktionen/Rechnungen';
import Belegerfassung from './pages/funktionen/Belegerfassung';
import Banking from './pages/funktionen/Banking';
import Umsatzsteuer from './pages/funktionen/Umsatzsteuer';
import BWA from './pages/funktionen/BWA';
import Auftraege from './pages/funktionen/Auftraege';
import Kontakte from './pages/funktionen/Kontakte';
import Produkte from './pages/funktionen/Produkte';
import Uebersicht from './pages/funktionen/Uebersicht';
import EuER from './pages/funktionen/EuER';
import Saldenliste from './pages/funktionen/Saldenliste';
import Exporte from './pages/funktionen/Exporte';
import Integrationen from './pages/funktionen/Integrationen';
import MobileApp from './pages/funktionen/MobileApp';

// Lösungen Pages
import Freelancer from './pages/loesungen/Freelancer';
import Kleinunternehmer from './pages/loesungen/Kleinunternehmer';
import Startups from './pages/loesungen/Startups';
import KMU from './pages/loesungen/KMU';
import Dienstleister from './pages/loesungen/Dienstleister';
import Ecommerce from './pages/loesungen/Ecommerce';
import Handwerk from './pages/loesungen/Handwerk';

// Other Pages
import PreisePage from './pages/Preise';
import Steuerberater from './pages/Steuerberater';
import Impressum from './pages/Impressum';
import Datenschutz from './pages/Datenschutz';
import AVV from './pages/AVV';
import Subprozessoren from './pages/Subprozessoren';
import AGB from './pages/AGB';
import UeberUns from './pages/UeberUns';
import Kontakt from './pages/Kontakt';
import Karriere from './pages/Karriere';
import Presse from './pages/Presse';
import Partner from './pages/Partner';
import Blog from './pages/Blog';
import Api from './pages/Api';
import Updates from './pages/Updates';
import Hilfe from './pages/Hilfe';
import Status from './pages/Status';
import Dokumentation from './pages/Dokumentation';
import Community from './pages/Community';
import Lizenzen from './pages/Lizenzen';

// Landing Page Component
function LandingPage(): ReactElement {
  return (
    <>
      <Hero />
      <Features />
      <HowItWorks />
      <Pricing />
      {/* Testimonials vorerst ausgeblendet: Die Stimmen waren erfunden
          ("Maria Schmidt, Schmidt Design GmbH") samt Fuenf-Sterne-Wertungen.
          Erfundene Kundenstimmen sind irrefuehrende Werbung (§ 5 UWG) und
          fallen Lesern auf. Wieder einblenden, sobald echte Zitate mit
          Einwilligung vorliegen. */}
      {/* <Testimonials /> */}
      <CallToAction />
    </>
  );
}

// Layout wrapper with Header and Footer
function Layout({ children }: { children: ReactElement }): ReactElement {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
}

// Marketing-Seiten (mit Header/Footer). Eigene Komponente, damit der
// eingeloggte Bereich NICHT in das Marketing-Layout gewickelt wird.
/**
 * Setzt Titel, Beschreibung und Canonical passend zur aktuellen Marketing-
 * Seite. Zentral statt in jeder der rund 37 Komponenten: weniger Stellen,
 * an denen ein Titel vergessen wird, und die Texte liegen gesammelt in
 * seoTexte.ts.
 */
function SeoFuerPfad(): null {
  const { pathname } = useLocation();
  // Ohne Eintrag bleiben die Werte aus index.html stehen — besser als ein
  // leerer Titel.
  const fallback = SEO['/']!;
  const angaben = SEO[pathname] ?? { ...fallback, pfad: pathname };
  useSeo(angaben);
  return null;
}

function MarketingRoutes(): ReactElement {
  return (
    <>
      <SeoFuerPfad />
      <Layout>
      <Routes>
        {/* Landing Page */}
        <Route path="/" element={<LandingPage />} />

        {/* Funktionen */}
        <Route path="/funktionen" element={<FunktionenUebersicht />} />
        <Route path="/funktionen/rechnungen" element={<Rechnungen />} />
        <Route path="/funktionen/belegerfassung" element={<Belegerfassung />} />
        <Route path="/funktionen/banking" element={<Banking />} />
        <Route path="/funktionen/umsatzsteuer" element={<Umsatzsteuer />} />
        <Route path="/funktionen/bwa" element={<BWA />} />

        <Route path="/funktionen/auftraege" element={<Auftraege />} />
        <Route path="/funktionen/kontakte" element={<Kontakte />} />
        <Route path="/funktionen/produkte" element={<Produkte />} />
        <Route path="/funktionen/uebersicht" element={<Uebersicht />} />
        <Route path="/funktionen/euer" element={<EuER />} />
        <Route path="/funktionen/saldenliste" element={<Saldenliste />} />
        <Route path="/funktionen/exporte" element={<Exporte />} />
        <Route path="/funktionen/integrationen" element={<Integrationen />} />
        <Route path="/funktionen/mobile-app" element={<MobileApp />} />

        {/* Lösungen */}
        <Route path="/loesungen/freelancer" element={<Freelancer />} />
        <Route path="/loesungen/kleinunternehmer" element={<Kleinunternehmer />} />
        <Route path="/loesungen/startups" element={<Startups />} />
        <Route path="/loesungen/kmu" element={<KMU />} />
        <Route path="/loesungen/dienstleister" element={<Dienstleister />} />
        <Route path="/loesungen/ecommerce" element={<Ecommerce />} />
        <Route path="/loesungen/handwerk" element={<Handwerk />} />

        {/* Weitere Seiten */}
        <Route path="/preise" element={<PreisePage />} />
        <Route path="/steuerberater" element={<Steuerberater />} />

        {/* Rechtliches */}
        <Route path="/impressum" element={<Impressum />} />
        <Route path="/datenschutz" element={<Datenschutz />} />
        <Route path="/avv" element={<AVV />} />
        <Route path="/subprozessoren" element={<Subprozessoren />} />
        <Route path="/agb" element={<AGB />} />
        <Route path="/lizenzen" element={<Lizenzen />} />

        {/* Unternehmen */}
        <Route path="/ueber-uns" element={<UeberUns />} />
        <Route path="/kontakt" element={<Kontakt />} />
        <Route path="/karriere" element={<Karriere />} />
        <Route path="/presse" element={<Presse />} />
        <Route path="/partner" element={<Partner />} />
        {/* Werbelink: setzt das Cookie und leitet zur Registrierung. */}
        <Route path="/einladung/:code" element={<Einladung />} />
        <Route path="/blog" element={<Blog />} />

        {/* Produkt & Support */}
        {/* NICHT "/api" — dieser Pfad gehoert dem Backend (404 vom Server). */}
        <Route path="/api-schnittstelle" element={<Api />} />
        <Route path="/updates" element={<Updates />} />
        <Route path="/hilfe" element={<Hilfe />} />
        <Route path="/status" element={<Status />} />
        <Route path="/dokumentation" element={<Dokumentation />} />
        <Route path="/community" element={<Community />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Layout>
    </>
  );
}

function App(): ReactElement {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <CookieConsent />
      <ThemeProvider>
      <AuthProvider>
        <CompanyProvider>
          <ToastProvider>
          <ConfirmProvider>
          <Routes>
            {/* Auth-Seiten — ohne Marketing-Layout */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            {/* Deutscher Alias: steht so in verteilten Werbelinks und ist
                die naheliegende Eingabe fuer deutschsprachige Besucher. */}
            <Route path="/registrieren" element={<Register />} />
            <Route path="/passwort-vergessen" element={<ForgotPassword />} />
            <Route path="/passwort-setzen" element={<SetPassword />} />

            {/* Eingeloggt, aber ggf. noch ohne Firma → Onboarding */}
            <Route element={<RequireAuth />}>
              <Route path="/onboarding" element={<Onboarding />} />

              {/* Geschützter App-Bereich: braucht Auth UND eine Firma */}
              <Route element={<RequireCompany />}>
                <Route path="/app" element={<AppLayout />}>
                  <Route index element={<Dashboard />} />
                  <Route path="kunden" element={<Customers />} />
                  <Route path="kunden/:id" element={<CustomerDetail />} />
                  <Route path="produkte" element={<Products />} />
                  <Route path="rechnungen" element={<Invoices />} />
                  <Route path="rechnungen/neu" element={<NewInvoice />} />
                  <Route path="rechnungen/:id/bearbeiten" element={<NewInvoice />} />
                  <Route path="rechnungen/:id" element={<InvoiceDetail />} />
                  <Route path="belege" element={<Receipts />} />
                  <Route path="kassenbuch" element={<Cashbook />} />
                  <Route path="nachweise" element={<Nachweise />} />
                  <Route path="buchhaltung" element={<Accounting />} />
                  <Route path="kennzahlen" element={<KPIs />} />
                  <Route path="steuern" element={<Taxes />} />
                  <Route path="banking" element={<BankingApp />} />
                  <Route path="formulare" element={<Forms />} />
                  <Route path="einstellungen" element={<Settings />} />
                  <Route path="einstellungen/tarif" element={<Billing />} />
                  <Route path="partner" element={<PartnerProgramm />} />
                  <Route path="mehr" element={<More />} />
                </Route>
              </Route>
            </Route>

            {/* Alles andere → Marketing-Site */}
            <Route path="/*" element={<MarketingRoutes />} />
          </Routes>
          </ConfirmProvider>
          </ToastProvider>
        </CompanyProvider>
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

// 404 Page
function NotFoundPage(): ReactElement {
  return (
    <div className="pt-20 min-h-screen">
      <div className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-6xl font-bold text-primary-600 mb-4">404</h1>
          <h2 className="text-2xl font-bold text-secondary-900 mb-4">
            Seite nicht gefunden
          </h2>
          <p className="text-lg text-secondary-600 mb-8">
            Die angeforderte Seite existiert leider nicht.
          </p>
          <a
            href="/"
            className="btn-primary px-6 py-3"
          >
            Zurück zur Startseite
          </a>
        </div>
      </div>
    </div>
  );
}

export default App;
